import type { DateStatus } from "@@/shared/date-time/date-time-selection/dates/date-selection-vt_v2/date-status";
import { useDateStylingWithCssProperties } from "@@/shared/date-time/date-time-selection/dates/date-selection-vt_v2/use-date-styling";
import { useTheme } from "@emotion/react";
import type { IsoAndUnixTimestamp } from "@towni/common";
import type React from "react";
import { useMemo } from "react";
import { dateCircleSize } from "./date-circle-size";
import { useDateToggleContext } from "./date-toggle.context";
import { useDateStatus } from "./use-date-status";

type Props = {
    date: IsoAndUnixTimestamp; // Should be zoned already
    dayOfMonthIndex: number;
    dayOfWeekIndex: number;
};

const _baseContainerCss = {
    width: "100%",
    minWidth: dateCircleSize + 2,
    minHeight: dateCircleSize,
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
} as React.CSSProperties;

const useContainerCss = (status: DateStatus) => {
    const theme = useTheme();
    const alternateBackgroundColor = useMemo(
        () =>
            theme.isLightTheme
                ? theme.colors.primary.main.value.withModifications({
                      saturation: 0.6,
                      light: 0.88,
                  })
                : theme.colors.primary.main.value.withModifications({
                      saturation: 0.5,
                      light: 0.75,
                      alpha: 0.75,
                  }),
        [theme.colors.primary.main.value, theme.isLightTheme],
    );

    const mappedStyle = useMemo(() => {
        const map = new Map<DateStatus, React.CSSProperties>();

        map.set("SELECTED__FIRST", {
            ..._baseContainerCss,
            background: `linear-gradient(
                90deg,
                ${theme.colors.transparent.asString} 50%,
                ${alternateBackgroundColor.asString} 50%
            )`,
        });
        map.set("SELECTED__LAST", {
            ..._baseContainerCss,
            background: `linear-gradient(
                90deg,
                ${alternateBackgroundColor.asString} 50%,
                ${theme.colors.transparent.asString} 50%
            )`,
        });
        map.set("SELECTED__PART_OF_RANGE", {
            ..._baseContainerCss,
            background: alternateBackgroundColor.asString,
        });
        map.set("SELECTED__SOLO", {
            ..._baseContainerCss,
            background: theme.colors.transparent.asString,
        });
        map.set("SELECTABLE__END_OF_RANGE", _baseContainerCss);
        map.set("SELECTABLE__PART_OF_NEW_RANGE", _baseContainerCss);

        return map;
    }, [alternateBackgroundColor.asString, theme.colors.transparent.asString]);

    return mappedStyle.get(status);
};

const DateCell = (props: Props) => {
    const theme = useTheme();
    const status = useDateStatus(props.date);
    const styling = useDateStylingWithCssProperties(status);
    const { toggle } = useDateToggleContext();

    const containerCss = useContainerCss(status);

    const isSelectableAsPartOfRange =
        status === "SELECTABLE__PART_OF_RANGE" ||
        status === "SELECTABLE__PART_OF_NEW_RANGE";
    const isUnavailable = status === "UNAVAILABLE";
    const isPassed = status === "PASSED";

    const onClick = (_event: React.MouseEvent) => {
        if (isSelectableAsPartOfRange) return;
        if (isUnavailable) return;
        if (isPassed) return;
        toggle(props.date);
    };

    return (
        <div style={containerCss}>
            <div style={styling} onClick={onClick}>
                <div
                    style={{
                        position: "relative",
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        userSelect: "none",
                        lineHeight: "1em",
                        fontSize: "1em",
                        fontWeight: 600,
                    }}>
                    {isUnavailable && (
                        <div
                            style={{
                                position: "absolute",
                                width: dateCircleSize * 0.8,
                                height: 2,
                                backgroundColor: theme.isLightTheme
                                    ? theme.colors.black.asString
                                    : theme.colors.white.asString,
                                transform: "rotate(135deg)",
                                opacity: 0.5,
                                zIndex: 10,
                            }}
                        />
                    )}
                    {props.dayOfMonthIndex}
                </div>
            </div>
        </div>
    );
};

export { DateCell };
